import { QueryEntity } from '@datorama/akita';
import { switchMap } from 'rxjs/operators';
import { InvoiceStatusState, invoiceStatusStore } from './invoice-status.store';
import { CreateInvoiceStatus, InvoiceSort, InvoiceStatus } from './invoice-status.model';

function sortBy(a: InvoiceStatus, b: InvoiceStatus, { ui: { sortBy } }: InvoiceStatusState) {
  switch (sortBy) {
    case InvoiceSort.CorporateAccount:
      return (a.corporateAccount ?? '') > (b.corporateAccount ?? '') ? 1 : -1;
    case InvoiceSort.Location:
      return (a.location ?? '') > (b.location ?? '') ? 1 : -1;
    case InvoiceSort.Status: {
      const comparison =
        getStatusNumber(a.createInvoiceStatus) - getStatusNumber(b.createInvoiceStatus);
      //Apply secondary sort of default
      if (comparison !== 0) return comparison;
    }
    // eslint-disable-next-line
    case InvoiceSort.InvoiceNumber:
    default:
      return sortInvoiceNumber(a.invoiceNumber, b.invoiceNumber);
  }
}

function getStatusNumber(status?: CreateInvoiceStatus): number {
  switch (status) {
    case CreateInvoiceStatus.NotStarted:
      return 1;
    case CreateInvoiceStatus.Running:
      return 2;
    case CreateInvoiceStatus.Warning:
      return 3;
    case CreateInvoiceStatus.SuccessWithWarnings:
      return 4;
    case CreateInvoiceStatus.Success:
      return 5;
    case CreateInvoiceStatus.NoTransactions:
      return 6;
    case CreateInvoiceStatus.Failed:
      return 7;
    default:
      return 0;
  }
}

function sortInvoiceNumber(a?: string | null, b?: string | null): number {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const regex = /\d*$/;
  const prefixRegex = /^.*(?=(-\d*$))/;
  const aMatch = regex.exec(a);
  const bmatch = regex.exec(b);
  const aEndNumbers = aMatch ? Number.parseInt(aMatch[0]) : undefined;
  const bEndNumbers = bmatch ? Number.parseInt(bmatch[0]) : undefined;

  if (aEndNumbers && bEndNumbers) {
    const aPrefix = prefixRegex.exec(a);
    const bPrefix = prefixRegex.exec(b);
    if (aPrefix && bPrefix) {
      const prefixComparison = aPrefix[0].localeCompare(bPrefix[0]);
      if (prefixComparison !== 0) return prefixComparison;
      return aEndNumbers - bEndNumbers;
    }
  }

  return a.localeCompare(b);
}

export class InvoiceStatusQuery extends QueryEntity<InvoiceStatusState> {
  corporateAccounts = this.selectAll().pipe(
    switchMap(a =>
      this.selectMany(
        a.map(s => s.id),
        s => ({ accountId: s.corporateAccountId ?? '', accountName: s.corporateAccount ?? '' })
      )
    )
  );

  invoiceStatuses = this.select(({ ui }) => ui).pipe(
    switchMap(ui =>
      this.selectAll({
        filterBy: [
          x => !ui.corporateAccountId || ui.corporateAccountId === x.corporateAccountId,
          x => !ui.invoiceRunId || ui.invoiceRunId === x.invoiceRunId,
          x =>
            !ui.createdStartDate ||
            (!!x.createdDate && ui.createdStartDate.getTime() <= Date.parse(x.createdDate)),
          x =>
            !ui.createdEndDate ||
            (!!x.createdDate && ui.createdEndDate.getTime() >= Date.parse(x.createdDate)),
          x =>
            !ui.createdStartDate ||
            (!!x.createdDate && ui.createdStartDate.getTime() <= Date.parse(x.createdDate)),
          x => !ui.status || (!!x.createInvoiceStatus && x.createInvoiceStatus === ui.status),
        ],
        sortBy,
      })
    )
  );

  loading = this.selectLoading();
}
export const invoiceStatusQuery = new InvoiceStatusQuery(invoiceStatusStore);
